// src/scene/cardLayout.ts
// "Quiet museum, loud art" frame geometry (world units). A warm-ivory mat with
// asymmetric gallery weighting — equal generous top + side margins, a deeper
// bottom margin (the weight line) — sitting inside a dark-room shadow gutter
// where the baked float shadow lives. The window opening adapts to art aspect.

/** Mat width in world units — the visible ivory "frame". */
export const MAT_W = 1.35;

const MAT_SIDE_FRAC = 0.115;      // equal top + left/right mat margin (× mat width)
const MAT_BOTTOM_FRAC = 0.205;    // deeper bottom margin: the weight line
const SHADOW_MARGIN_FRAC = 0.085; // dark-room gutter holding the baked float shadow
const WIN_MIN = 0.62;             // window height floor (× window width) — landscape
const WIN_MAX = 1.62;             // window height ceiling — portrait

export interface CardLayout {
  aspect: number;
  matW: number; matH: number;     // the ivory mat
  cardW: number; cardH: number;   // mat + shadow margin → the outer plane geometry
  winW: number; winH: number;     // the window opening → art + ring geometry
  winCenterY: number;             // window centre offset ABOVE the card centre (asymmetry)
  margin: number; side: number; bottom: number;
}

/** Asymmetric mat layout from art aspect (w/h). All values in world units. */
export function layoutCard(aspect: number): CardLayout {
  const matW = MAT_W;
  const side = matW * MAT_SIDE_FRAC;
  const bottom = matW * MAT_BOTTOM_FRAC;

  const winW = matW - side * 2;
  // window height follows aspect, clamped so very tall/wide art stays coherent
  const winH = Math.max(winW * WIN_MIN, Math.min(winW * WIN_MAX, winW / aspect));

  const matH = side + winH + bottom;          // top margin == side margin
  const margin = matW * SHADOW_MARGIN_FRAC;
  const cardW = matW + margin * 2;
  const cardH = matH + margin * 2;

  // window centre measured from card top = margin + side + winH/2; the card
  // centre is cardH/2, so the window sits this far ABOVE centre (top<bottom margin).
  const winCenterY = cardH / 2 - (margin + side + winH / 2);

  return { aspect, matW, matH, cardW, cardH, winW, winH, winCenterY, margin, side, bottom };
}
